import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import {
  Row,
  Card,
  Space,
  Spinner,
  ProgressBar,
} from "@masterteam/mt-design-react";

import EmptyData from "../../EmptyData";
import Breadcrumb from "../../Breadcrumb";
import ObjectiveCard from "../ObjectiveCard";
import KPIStatusIndicator from "../../KPIStatusIndicator";
import DoughnutChart from "../../DoughnutChart";

import api from "../../../providers/api";
import { addToast } from "../../../helpers";
import useIsAr from "../../../Hooks/useIsAr";

import "./style.scss";

const ObjectiveDetails = () => {
  const intl = useIntl();
  const [isAr] = useIsAr();
  const { id } = useParams();

  const [data, setData] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const status = statuses.find((e) => e.Code === data.Status);

  const getData = async () => {
    setDataLoading(true);
    try {
      const [dataRes, statusesRes] = await Promise.all([
        api.kpiDashboard.getDataStrategicObjectiveId(id),
        api.handshake.getStatuses(),
      ]);

      setData(dataRes.Data);
      setStatuses(statusesRes.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  if (dataLoading)
    return (
      <Row justify="center" style={{ marginTop: "24px" }}>
        <Spinner size="large" />
      </Row>
    );

  return (
    <Space gap={32} direction="vertical" className="objectives-list-wrapper">
      <div>
        <Breadcrumb
          data={[
            <FormattedMessage id="common.kpis" />,
            <FormattedMessage id="common.strategic-objectives" />,
          ]}
          fontSize="var(--font-large)"
        />

        <Row justify="end">
          <Space size="medium">
            <div className="kpi-label">
              <FormattedMessage id="common.kpi-status" /> :
            </div>
            <Space size="medium">
              {statuses.map((_item) => (
                <KPIStatusIndicator
                  item={_item}
                  list={statuses}
                  key={_item?.ID}
                />
              ))}
            </Space>
          </Space>
        </Row>
      </div>

      <Card className="info">
        <div className="code" style={{ backgroundColor: status.Color }}>
          {data.Code}
        </div>
        <Row justify="between" align="center" className="name-wrapper">
          <Space gap={0} className="name-wrapper" align="center">
            <div>{isAr ? data?.ArabicName : data?.EnglishName}</div>
          </Space>
          <Row className="progress" gap="5px">
            <ProgressBar value={data?.Performance} color={status.Color} />
            <span
              style={{ color: status?.Color }}
            >{`${data?.Performance} %`}</span>
          </Row>
        </Row>
      </Card>

      <Card direction="vertical" className="kpi-chart">
        <div className="header-title">
          <p>
            {intl.formatMessage({
              id: "kpis-dashboard.kpi-by-status",
            })}
          </p>
        </div>
        <div style={{ width: "300px", padding: "16px" }}>
          <DoughnutChart
            data={data?.KPIStatuses || []}
            statuses={statuses}
            count={data?.KPICount}
            title={intl.formatMessage({ id: "common.kpis" })}
          />
        </div>
      </Card>

      <Row className="title-wrapper">
        <h2>{intl.formatMessage({ id: "common.sub-strategic-objectives" })}</h2>
      </Row>

      {data?.Attribute?.length > 0 ? (
        <div
          className={`objectives-list-content animated ${
            isAr ? "fadeInLeft" : "fadeInRight"
          }`}
        >
          {data.Attribute.map((item) => (
            <ObjectiveCard
              key={item?.ID}
              data={item}
              statuses={statuses}
              isAttribute
            />
          ))}
        </div>
      ) : (
        <EmptyData title={<FormattedMessage id="no-data" />} />
      )}
    </Space>
  );
};

export default ObjectiveDetails;
